import {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    useCallback,
    type ReactNode,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/auth-store';

// Socket.io server URL
// In production, use relative path so nginx can proxy WebSocket connections
// In development, use full URL
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || (import.meta.env.PROD ? '' : 'http://localhost:8000');

// ============================================
// TYPES
// ============================================

export interface SocketContextType {
    socket: Socket | null;
    isConnected: boolean;
    joinProject: (projectId: string) => void;
    leaveProject: (projectId: string) => void;
    joinTask: (taskId: string) => void;
    leaveTask: (taskId: string) => void;
    startTyping: (taskId: string) => void;
    stopTyping: (taskId: string) => void;
}

interface TaskCreatedEvent {
    task: {
        id: string;
        title: string;
        status: string;
        priority: string;
        projectId: string;
        dueDate: string | null;
        assignees?: Array<{
            id: string;
            userId: string;
            user: {
                id: string;
                name: string;
                avatarUrl: string | null;
            };
        }>;
    };
    actorId: string;
    actorName: string;
}

interface TaskUpdatedEvent extends TaskCreatedEvent {
    changes?: Record<string, any>;
}

interface TaskDeletedEvent {
    taskId: string;
    deletedBy: string;
}

interface TaskMovedEvent {
    taskId: string;
    newOrder: number;
    newParentId: string | null;
    movedBy: string;
}

interface TaskAssignedEvent {
    taskId: string;
    assigneeId: string;
}

interface TaskUnassignedEvent {
    taskId: string;
    assigneeId: string;
}

interface ProjectDataImportedEvent {
    projectId: string;
}

interface CommentAddedEvent {
    taskId: string;
    comment: {
        id: string;
        content: string;
        authorId: string;
        author: {
            id: string;
            name: string;
            avatarUrl: string | null;
        };
        createdAt: string;
    };
}

interface CommentUpdatedEvent {
    commentId: string;
    taskId: string;
    content: string;
    editedAt: string;
}

interface CommentDeletedEvent {
    commentId: string;
    taskId: string;
}

interface AttachmentAddedEvent {
    taskId: string;
    attachment: {
        id: string;
        filename: string;
        originalName: string;
        mimeType: string;
        size: number;
        url: string;
    };
}

interface AttachmentDeletedEvent {
    taskId: string;
    attachmentId: string;
}

interface MemberJoinedEvent {
    projectId: string;
    member: {
        id: string;
        userId: string;
        role: string;
        user: {
            id: string;
            name: string;
            avatarUrl: string | null;
        };
    };
}

interface MemberLeftEvent {
    projectId: string;
    memberId: string;
    userId: string;
}

interface ProjectDeletedEvent {
    projectId: string;
}

interface TypingEvent {
    taskId: string;
    userId: string;
    userName?: string;
}

interface NotificationEvent {
    id: string;
    type: string;
    title: string;
    message: string;
    isRead: boolean;
    data: Record<string, any> | null;
    createdAt: string;
}

// Export event types for use in other files
export type {
    TaskCreatedEvent,
    TaskUpdatedEvent,
    TaskDeletedEvent,
    TaskMovedEvent,
    TaskAssignedEvent,
    TaskUnassignedEvent,
    ProjectDataImportedEvent,
    CommentAddedEvent,
    CommentUpdatedEvent,
    CommentDeletedEvent,
    AttachmentAddedEvent,
    AttachmentDeletedEvent,
    MemberJoinedEvent,
    MemberLeftEvent,
    ProjectDeletedEvent,
    TypingEvent,
    NotificationEvent,
};

// ============================================
// CONTEXT
// ============================================

const SocketContext = createContext<SocketContextType>({
    socket: null,
    isConnected: false,
    joinProject: () => { },
    leaveProject: () => { },
    joinTask: () => { },
    leaveTask: () => { },
    startTyping: () => { },
    stopTyping: () => { },
});

// ============================================
// PROVIDER
// ============================================

export function SocketProvider({ children }: { children: ReactNode }) {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const { user, isAuthenticated } = useAuthStore();
    const joinedRoomsRef = useRef<Set<string>>(new Set());

    // Initialize socket connection
    useEffect(() => {
        if (!isAuthenticated || !user) {
            // Disconnect if user logs out
            if (socket) {
                socket.disconnect();
                setSocket(null);
                setIsConnected(false);
                joinedRoomsRef.current.clear();
            }
            return;
        }

        // Create socket connection
        const newSocket = io(SOCKET_URL, {
            withCredentials: true,
            transports: ['websocket', 'polling'],
            autoConnect: true,
        });

        newSocket.on('connect', () => {
            setIsConnected(true);

            // Join user's personal room for notifications (also handles reconnection)
            if (user?.id) {
                newSocket.emit('join:user', user.id);
            }

            // Rejoin any previously joined project rooms
            joinedRoomsRef.current.forEach(roomKey => {
                if (roomKey.startsWith('project:')) {
                    const projectId = roomKey.replace('project:', '');
                    newSocket.emit('join:project', projectId);
                } else if (roomKey.startsWith('task:')) {
                    const taskId = roomKey.replace('task:', '');
                    newSocket.emit('join:task', taskId);
                }
            });
        });

        newSocket.on('disconnect', () => {
            setIsConnected(false);
        });

        newSocket.on('connect_error', (error) => {
            console.error('Socket connection error:', error);
            setIsConnected(false);
        });

        setSocket(newSocket);

        // Cleanup on unmount
        return () => {
            if (user?.id) {
                newSocket.emit('leave:user', user.id);
            }
            newSocket.disconnect();
            joinedRoomsRef.current.clear();
        };
    }, [isAuthenticated, user?.id]);

    // Join project room
    const joinProject = useCallback(
        (projectId: string) => {
            if (socket && isConnected && projectId) {
                const roomKey = `project:${projectId}`;
                if (!joinedRoomsRef.current.has(roomKey)) {
                    socket.emit('join:project', projectId);
                    joinedRoomsRef.current.add(roomKey);
                }
            }
        },
        [socket, isConnected]
    );

    // Leave project room
    const leaveProject = useCallback(
        (projectId: string) => {
            if (socket && projectId) {
                const roomKey = `project:${projectId}`;
                if (joinedRoomsRef.current.has(roomKey)) {
                    socket.emit('leave:project', projectId);
                    joinedRoomsRef.current.delete(roomKey);
                }
            }
        },
        [socket]
    );

    // Join task room
    const joinTask = useCallback(
        (taskId: string) => {
            if (socket && isConnected && taskId) {
                const roomKey = `task:${taskId}`;
                if (!joinedRoomsRef.current.has(roomKey)) {
                    socket.emit('join:task', taskId);
                    joinedRoomsRef.current.add(roomKey);
                }
            }
        },
        [socket, isConnected]
    );

    // Leave task room
    const leaveTask = useCallback(
        (taskId: string) => {
            if (socket && taskId) {
                const roomKey = `task:${taskId}`;
                if (joinedRoomsRef.current.has(roomKey)) {
                    socket.emit('leave:task', taskId);
                    joinedRoomsRef.current.delete(roomKey);
                }
            }
        },
        [socket]
    );

    // Start typing indicator
    const startTyping = useCallback(
        (taskId: string) => {
            if (socket && isConnected && taskId && user) {
                socket.emit('typing:start', {
                    taskId,
                    userId: user.id,
                    userName: user.name,
                });
            }
        },
        [socket, isConnected, user]
    );

    // Stop typing indicator
    const stopTyping = useCallback(
        (taskId: string) => {
            if (socket && taskId && user) {
                socket.emit('typing:stop', {
                    taskId,
                    userId: user.id,
                });
            }
        },
        [socket, user]
    );

    return (
        <SocketContext.Provider
            value={{
                socket,
                isConnected,
                joinProject,
                leaveProject,
                joinTask,
                leaveTask,
                startTyping,
                stopTyping,
            }}
        >
            {children}
        </SocketContext.Provider>
    );
}

// ============================================
// HOOK
// ============================================

export function useSocket() {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocket must be used within a SocketProvider');
    }
    return context;
}

// ============================================
// EVENT SUBSCRIPTION HOOK
// ============================================

export function useSocketEvent<T>(
    event: string,
    callback: (data: T) => void,
    deps: React.DependencyList = []
) {
    const { socket } = useSocket();
    // Use a ref to always have the latest callback without re-subscribing
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        if (!socket) {
            console.warn(`[Socket] Cannot listen to ${event}: socket not connected`);
            return;
        }

        const wrappedCallback = (data: T) => {
            callbackRef.current(data);
        };

        socket.on(event, wrappedCallback);

        return () => {
            socket.off(event, wrappedCallback);
        };
    }, [socket, event, ...deps]);
}
